"use client";

import { motion, AnimatePresence } from "motion/react";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowLeft,
  Clock,
  MapPin,
  Users,
  CheckCircle2,
  Star,
  Calendar,
  Package,
  Utensils,
  Bed,
  Car,
  Award,
  X,
  User,
  Mail,
  Phone,
  MessageSquare,
  ChevronDown,
  Play,
  Video,
  Camera,
  ChevronRight,
  ChevronLeft,
  Plus,
  Minus,
} from "lucide-react";
import { useEffect, useState, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import {
  insertBookingReference,
  insertWebsiteInquiry,
} from "@/services/hotel.services";
import { ErrorToast } from "../common/Toast";
import {
  generateValueLabelRange,
  formatCurrency,
  cleanHeaderTags,
} from "@/_helper/helper";
import { ISettings } from "@/store/homeSlice";
import InquiryModal from "../common/InquiryModal";
import { getPackageByURLAPI } from "@/services/package.services";
import Slider from "react-slick";

export default function PackageDetailClient({
  packageData,
}: {
  packageData: any;
}) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const loading = useAppSelector((state) => state.hotel.loading);
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [nav1, setNav1] = useState<Slider | null>(null);
  const [nav2, setNav2] = useState<Slider | null>(null);
  const slider1 = useRef<Slider>(null);
  const slider2 = useRef<Slider>(null);
  const [activeImages, setActiveImages] = useState<any[]>([]);
  const [activeTitle, setActiveTitle] = useState<string>("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number | null>(
    null,
  );
  const [currentVideoSlide, setCurrentVideoSlide] = useState(0);
  const [navVideo1, setNavVideo1] = useState<Slider | null>(null);
  const [navVideo2, setNavVideo2] = useState<Slider | null>(null);
  const sliderVideo1 = useRef<Slider>(null);
  const sliderVideo2 = useRef<Slider>(null);

  const getYouTubeID = (url: string) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  useEffect(() => {
    if (selectedIndex !== null) {
      setNav1(slider1.current);
      setNav2(slider2.current);
      setCurrentSlide(selectedIndex);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSelectedIndex(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else if (selectedVideoIndex === null) {
      document.body.style.overflow = "unset";
    }
  }, [selectedIndex, activeImages]);

  useEffect(() => {
    if (selectedVideoIndex !== null) {
      setNavVideo1(sliderVideo1.current);
      setNavVideo2(sliderVideo2.current);
      setCurrentVideoSlide(selectedVideoIndex);
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setSelectedVideoIndex(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else if (selectedIndex === null) {
      document.body.style.overflow = "unset";
    }
  }, [selectedVideoIndex, packageData?.VideoGallery]);

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [openDay, setOpenDay] = useState<number | null>(0);

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Package Not Found
          </h1>
          <Link
            href="/gujarat-packages"
            className="text-orange-600 hover:text-orange-700"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const duration = `${packageData.NoOfNights || 0} Nights / ${packageData.NoOfDays || 0} Days`;
  const mainCost = packageData.TourCost?.[0] || {};
  // const highlights = packageData.KeyPoints?.split(",")?.filter(Boolean) || [];
  const IsGujarat = packageData.PackageType === "GUJARAT";

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative h-[65vh] overflow-hidden">
          <Image
            src={packageData?.BannerImage || packageData?.MainImage}
            alt={packageData?.TourName}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0 pointer-events-none"></div>

          <div className="absolute inset-0 flex items-end pointer-events-none pb-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="pointer-events-auto"
              >
                {/* <Link
                  href={IsGujarat ? "/gujarat-packages" : "/sou-packages"}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Packages</span>
                </Link>
                <br />
                <div className="inline-block bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                  {packageData.PackageType} PACKAGE
                </div> */}

                <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-2xl">
                  {packageData.TourName}
                </h1>
                {/* <div
                  className="text-xl md:text-2xl text-gray-200 max-w-3xl drop-shadow-lg"
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(packageData.ShortDescription),
                  }}
                /> */}

                {/* Quick Info Bar */}
                <div className="flex flex-wrap gap-6 mt-8">
                  <div className="flex items-center gap-2 text-white bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <Calendar className="w-5 h-5 text-amber-400" />
                    <span className="font-medium">{duration}</span>
                  </div>
                  {mainCost.PickupLocation && (
                    <div className="flex items-center gap-2 text-white bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <MapPin className="w-5 h-5 text-amber-400" />
                      <span className="font-medium">
                        {mainCost.PickupLocation}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview */}
              {packageData.LongDescription && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                    <Package className="w-8 h-8 text-orange-600" />
                    Package Overview
                  </h2>
                  <div
                    className="text-lg text-gray-700 leading-relaxed blog-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(packageData.LongDescription),
                    }}
                  />
                </motion.section>
              )}

              {/* Package Highlights */}
              {packageData.Highlights && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Star className="w-8 h-8 text-orange-600" />
                    Package Highlights
                  </h2>
                  {/* {highlights && highlights?.length > 0 && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {highlights.map((highlight: string, index: number) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-sm"
                        >
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium">
                            {highlight}
                          </span>
                        </div>
                      ))}
                    </div>
                  )} */}
                  <div
                    className="mt-6 blog-content highlights-content"
                    dangerouslySetInnerHTML={{
                      __html: cleanHeaderTags(packageData.Highlights),
                    }}
                  />
                </motion.section>
              )}

              {/* Accommodation Details */}
              {mainCost.RoomTypeName && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
                >
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Bed className="w-8 h-8 text-orange-600" />
                    Accommodation
                  </h2>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {mainCost.RoomTypeName}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {packageData.PackageType}
                    </p>
                  </div>
                </motion.section>
              )}

              {/* Detailed Itinerary */}
              {packageData.TourItinerary?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <Clock className="w-6 h-6 md:w-8 md:h-8 text-orange-600" />
                    Detailed Itinerary
                  </h2>
                  <div className="space-y-3">
                    {packageData.TourItinerary.map(
                      (day: any, dayIndex: number) => {
                        const isOpen = openDay === dayIndex;
                        return (
                          <div
                            key={dayIndex}
                            className="rounded-lg overflow-hidden"
                          >
                            {/* Accordion Header */}
                            <button
                              onClick={() =>
                                setOpenDay(isOpen ? null : dayIndex)
                              }
                              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 md:px-6 py-3 md:py-4 font-bold text-base md:text-xl flex items-center justify-between gap-3 cursor-pointer rounded-lg shadow-md hover:shadow-lg transition-all"
                            >
                              <span className="line-clamp-1 text-left">
                                Day {day.DayNo}: {day.Title}
                              </span>
                              <ChevronDown
                                className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${
                                  isOpen ? "rotate-180" : "rotate-0"
                                }`}
                              />
                            </button>
                            {/* Accordion Body */}
                            <div
                              className={`overflow-hidden transition-all duration-300 ${
                                isOpen
                                  ? "max-h-[2000px] opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="md:ml-4 mt-3 mb-4">
                                <div className="flex gap-4 relative">
                                  <div className="flex-1 bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-100">
                                    <div
                                      className="text-gray-700 text-sm md:text-base blog-content"
                                      dangerouslySetInnerHTML={{
                                        __html: cleanHeaderTags(
                                          day.Description,
                                        ),
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </motion.section>
              )}

              {/* Inclusions & Exclusions */}
              {(packageData.Exclusion || packageData.Inclusion) && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  {packageData.Inclusion && (
                    <div className="bg-green-50 rounded-2xl shadow-lg p-8 border border-green-100">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                        Package Includes
                      </h3>
                      <div
                        className="space-y-2 ticket-desc text-gray-700 blog-content"
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(packageData.Inclusion),
                        }}
                      />
                    </div>
                  )}

                  {packageData.Exclusion && (
                    <div className="bg-red-50 rounded-2xl shadow-lg p-8 border border-red-100">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Award className="w-6 h-6 text-red-600" />
                        Not Included
                      </h3>
                      <div
                        className="space-y-2 blog-content text-gray-700"
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(packageData.Exclusion),
                        }}
                      />
                    </div>
                  )}
                </motion.section>
              )}

              {/* Photo Gallery - Matching Hotel Style */}
              {packageData?.Innerimage &&
                packageData?.Innerimage?.length > 0 && (
                  <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                  >
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                      <h2 className="text-xl md:text-3xl font-bold text-gray-900 m-0">
                        Photo Gallery
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {packageData.Innerimage.map((img: any, idx: number) => (
                        <div
                          key={idx}
                          className="relative aspect-square rounded-xl overflow-hidden group cursor-pointer shadow-sm"
                          onClick={() => {
                            setActiveImages(
                              packageData.Innerimage.map((i: any) => i.Images),
                            );
                            setActiveTitle("Photo Gallery");
                            setSelectedIndex(idx);
                          }}
                        >
                          <Image
                            src={img.Images}
                            alt={`Package View ${idx + 1}`}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                            <span className="text-white font-semibold text-sm">
                              View Image
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.section>
                )}

              {/* Video Gallery Section */}
              {packageData?.VideoGallery &&
                packageData?.VideoGallery?.length > 0 && (
                  <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-orange-50/30 rounded-3xl p-4 md:p-8 border border-orange-100 mb-10"
                  >
                    <div className="flex items-center gap-3 mb-8">
                      <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
                      <h2 className="text-xl md:text-3xl font-bold text-gray-900 m-0">
                        Video Gallery
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {packageData?.VideoGallery?.map(
                        (video: any, index: number) => {
                          const videoId = getYouTubeID(video.VideoURL);
                          return (
                            <motion.div
                              key={index}
                              whileHover={{ y: -5 }}
                              className="relative group cursor-pointer aspect-video rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:border-orange-500/30 transition-all duration-300"
                              onClick={() => {
                                setSelectedVideoIndex(index);
                                setCurrentVideoSlide(index);
                              }}
                            >
                              <Image
                                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                alt="Video Thumbnail"
                                fill
                                className="object-cover transition-transform duration-700 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                <div className="w-12 h-12 md:w-16 md:h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:scale-110 group-hover:bg-orange-600">
                                  <Play className="w-6 h-6 md:w-8 md:h-8 text-white fill-white ml-0.5 md:ml-1" />
                                </div>
                              </div>
                              <div className="absolute bottom-3 left-4 md:bottom-4 md:left-6 right-6">
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[10px] md:text-xs font-semibold uppercase tracking-wider border border-white/10">
                                  <Play size={10} className="fill-white" />
                                  Watch Video
                                </div>
                              </div>
                            </motion.div>
                          );
                        },
                      )}
                    </div>
                  </motion.section>
                )}

              {/* FAQ Section */}
              {packageData?.FAQ && packageData?.FAQ?.length > 0 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="bg-orange-50/30 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-orange-100"
                >
                  <div className="flex items-center gap-3 mb-6 md:mb-8">
                    <div className="w-1.5 h-6 md:h-8 bg-orange-500 rounded-full"></div>
                    <h2 className="text-xl md:text-3xl font-bold text-gray-900 m-0">
                      FAQ
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {packageData?.FAQ?.map((faq: any, index: number) => {
                      const isOpen = openFaqIndex === index;
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                            isOpen
                              ? "bg-white border-orange-200 shadow-md ring-1 ring-orange-500/10"
                              : "bg-white/50 border-gray-100 hover:border-orange-200"
                          }`}
                        >
                          <button
                            onClick={() =>
                              setOpenFaqIndex(isOpen ? null : index)
                            }
                            className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-between text-left group"
                          >
                            <div className="flex items-center gap-3 md:gap-4">
                              <div
                                className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-colors font-bold text-xs md:text-sm ${
                                  isOpen
                                    ? "bg-orange-500 text-white"
                                    : "bg-orange-100 text-orange-600 group-hover:bg-orange-500 group-hover:text-white"
                                }`}
                              >
                                {index + 1}
                              </div>
                              <h3
                                className={`text-base md:text-lg font-bold transition-colors ${
                                  isOpen
                                    ? "text-orange-600"
                                    : "text-gray-900 group-hover:text-orange-600"
                                }`}
                              >
                                {faq.Question}
                              </h3>
                            </div>
                            <div
                              className={`p-1.5 rounded-full transition-all duration-300 ${
                                isOpen
                                  ? "bg-orange-100"
                                  : "bg-gray-100 group-hover:bg-orange-100"
                              }`}
                            >
                              {isOpen ? (
                                <Minus className="w-4 h-4 text-orange-600 transition-colors" />
                              ) : (
                                <Plus className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors" />
                              )}
                            </div>
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{
                                  duration: 0.3,
                                  ease: "easeInOut",
                                }}
                              >
                                <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0 ml-10 md:ml-12">
                                  <div
                                    className="text-gray-600 leading-relaxed text-sm md:text-base blog-content border-t border-orange-50 border-dashed pt-4"
                                    dangerouslySetInnerHTML={{
                                      __html: cleanHeaderTags(faq.Answer),
                                    }}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.section>
              )}
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="sticky top-24 bg-white rounded-2xl shadow-2xl p-4 md:p-6 border-2 border-orange-100 mt-8 lg:mt-0"
              >
                {packageData.TourCost && packageData.TourCost?.length > 0 && (
                  <div className="mb-6">
                    <div className="text-sm text-gray-600 mb-2">
                      Starting from
                    </div>
                    {((IsGujarat ? +mainCost.TwoPax : +mainCost.TwoSharing) ||
                      0) > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-4xl font-bold text-gray-900">
                          {formatCurrency(
                            IsGujarat
                              ? mainCost.TwoPax || 0
                              : mainCost.TwoSharing || 0,
                          )}
                        </span>
                        <span className="text-gray-600">/ per couple</span>
                      </div>
                    ) : (
                      <div className="text-sm font-bold text-orange-600">
                        For Rate Call us on :
                        <span className="block text-2xl mt-1">
                          <Link
                            href={`tel:${PhoneNo}`}
                            className="hover:underline"
                          >
                            {PhoneNo}
                          </Link>
                        </span>
                      </div>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      Based on double occupancy
                    </p>
                  </div>
                )}

                {packageData.TourCost && packageData.TourCost?.length > 0 && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-6">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-orange-600" />
                      Pricing Options
                    </h4>
                    <div className="space-y-2 text-sm">
                      {IsGujarat ? (
                        <>
                          {mainCost.TwoPax && +mainCost.TwoPax > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Double Sharing
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.TwoPax)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.FourPax && +mainCost.FourPax > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Four Sharing
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.FourPax)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.SixPax && +mainCost.SixPax > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Six Sharing</span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.SixPax)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.EightPax && +mainCost.EightPax > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Eight Sharing
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.EightPax)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.TenPax && +mainCost.TenPax > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Ten Sharing</span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.TenPax)}
                              </span>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {mainCost.TwoSharing && +mainCost.TwoSharing > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Double Sharing
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.TwoSharing)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.ExtraAdults && +mainCost.ExtraAdults > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Extra Adult</span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.ExtraAdults)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.CWB && +mainCost.CWB > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Child (with bed)
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.CWB)}
                              </span>
                            </div>
                          ) : null}
                          {mainCost.CNB && +mainCost.CNB > 0 ? (
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Child (without bed)
                              </span>
                              <span className="font-bold text-gray-900">
                                {formatCurrency(mainCost.CNB)}
                              </span>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Star className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1">
                        Best Price Guarantee
                      </h5>
                      <p className="text-xs text-gray-600">
                        Book now and get the lowest rates with instant
                        confirmation
                      </p>
                    </div>
                  </div>
                </div>

                {packageData?.BookingURL && (
                  <button
                    onClick={async () => {
                      // Track redirect before opening third-party URL
                      insertBookingReference(
                        packageData?.ID,
                        window.location.href,
                      ).catch(() => {});
                      window.open(
                        packageData?.BookingURL,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                    className="block text-center w-full py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 mb-4 cursor-pointer"
                  >
                    Book This Package
                  </button>
                )}
                <button
                  onClick={() => setShowInquiryModal(true)}
                  className="w-full py-4 border border-amber-500 text-amber-500 hover:bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:text-white font-bold text-lg rounded-xl hover:shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 mb-4 cursor-pointer"
                >
                  Inquire Now
                </button>

                <div className="space-y-3 text-sm mb-6">
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Instant confirmation</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Flexible date changes</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>24/7 customer support</span>
                  </div>
                </div>

                {/* <div className="pt-6 border-t border-gray-200">
                <Link
                  href="/contact"
                  className="w-full py-3 border-2 border-orange-500 text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-all duration-300 flex items-center justify-center gap-2 text-center"
                >
                  <Car className="w-5 h-5" />
                  Customize This Package
                </Link>
              </div> */}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      <InquiryModal
        isOpen={showInquiryModal}
        onClose={() => setShowInquiryModal(false)}
        title="Inquiry for Package"
        subtitle={packageData.TourName}
        tourURL={packageData.TourURL}
        defaultInquiryType={"Gujarat Packages"}
        // defaultInquiryType={IsGujarat ? "Gujarat Packages" : "SOU Packages"}
        defaultPackageName={packageData?.TourName}
      />

      {/* Video Gallery Modal */}
      <AnimatePresence>
        {selectedVideoIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                  <h3 className="text-lg md:text-2xl font-bold tracking-tight">
                    <div> {packageData.TourName}</div>
                    <span className="text-orange-500">Video Gallery</span>
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedVideoIndex(null)}
                className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                aria-label="Close modal"
              >
                <X
                  className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6"
                />
              </button>
            </div>

            {/* Sliders Area */}
            <div className="flex-1 flex flex-col mt-20 justify-center items-center p-2 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
              {/* Main Slider */}
              <div className="w-full relative px-2 md:px-20 mb-8">
                <Slider
                  asNavFor={navVideo2!}
                  ref={sliderVideo1}
                  initialSlide={selectedVideoIndex}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={true}
                  fade={true}
                  nextArrow={<NextArrow />}
                  prevArrow={<PrevArrow />}
                  beforeChange={(_, next) => setCurrentVideoSlide(next)}
                  className="w-full gallery-main-slider"
                >
                  {packageData?.VideoGallery?.map(
                    (video: any, index: number) => {
                      const videoID = getYouTubeID(video.VideoURL);
                      return (
                        <div
                          key={index}
                          className="relative outline-none flex justify-center"
                        >
                          <div className="relative w-full aspect-video md:h-[65vh] rounded-xl md:rounded-3xl overflow-hidden bg-black shadow-2xl">
                            {currentVideoSlide === index && (
                              <iframe
                                src={`https://www.youtube.com/embed/${videoID}?autoplay=1&rel=0`}
                                className="w-full h-full border-0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            )}
                            {currentVideoSlide !== index && (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image
                                  src={`https://img.youtube.com/vi/${videoID}/maxresdefault.jpg`}
                                  alt="Video Thumbnail"
                                  fill
                                  className="object-cover opacity-50"
                                />
                                <Play className="w-20 h-20 text-white/50" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    },
                  )}
                </Slider>
              </div>

              {/* Thumbnails Slider */}
              <div className="w-full max-w-xl px-6 h-20 md:h-24">
                <Slider
                  asNavFor={navVideo1!}
                  ref={sliderVideo2}
                  initialSlide={selectedVideoIndex}
                  slidesToShow={
                    packageData?.VideoGallery?.length < 5
                      ? packageData?.VideoGallery?.length
                      : 5
                  }
                  swipeToSlide={true}
                  focusOnSelect={true}
                  infinite={packageData?.VideoGallery?.length > 1}
                  centerMode={true}
                  centerPadding="0px"
                  variableWidth={true}
                  arrows={false}
                  className="thumb-nav-slider"
                  responsive={[
                    { breakpoint: 1024, settings: { slidesToShow: 5 } },
                    { breakpoint: 768, settings: { slidesToShow: 4 } },
                    { breakpoint: 480, settings: { slidesToShow: 3 } },
                  ]}
                >
                  {packageData?.VideoGallery?.map(
                    (video: any, index: number) => {
                      const videoID = getYouTubeID(video.VideoURL);
                      return (
                        <div
                          key={index}
                          className="px-2 py-1 focus:outline-none cursor-pointer group !w-auto"
                        >
                          <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105 shadow-sm">
                            <Image
                              src={`https://img.youtube.com/vi/${videoID}/default.jpg`}
                              alt="Thumbnail"
                              fill
                              className="object-cover"
                              sizes="100px"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Play className="w-6 h-6 text-white opacity-80" />
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                </Slider>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Gallery Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-xl flex flex-col overflow-hidden"
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center px-4 sm:px-8 py-4 sm:py-6 text-white absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 via-black/30 to-transparent">
              <div className="flex items-center gap-4 sm:gap-6">
                <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                    <div> {packageData.TourName}</div>
                    <span className="text-orange-500">Photo Gallery</span>
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedIndex(null)}
                className="bg-white/10 hover:bg-orange-600 p-2.5 sm:p-3 rounded-full backdrop-blur-lg transition-all group border border-white/10 flex-shrink-0"
                aria-label="Close modal"
              >
                <X className="text-white group-hover:rotate-90 transition-transform duration-500 w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Modal Sliders */}
            <div className="flex-1 flex flex-col mt-20 justify-center items-center p-4 md:p-8 w-full max-w-[1400px] mx-auto overflow-hidden">
              {/* Main Slider */}
              <div className="w-full relative px-6 md:px-20 mb-8 max-h-[70vh]">
                <Slider
                  asNavFor={nav2!}
                  ref={slider1}
                  initialSlide={selectedIndex}
                  infinite={true}
                  speed={500}
                  slidesToShow={1}
                  slidesToScroll={1}
                  arrows={true}
                  nextArrow={<NextArrow />}
                  prevArrow={<PrevArrow />}
                  fade={true}
                  afterChange={(current) => setCurrentSlide(current)}
                  className="w-full gallery-main-slider"
                >
                  {activeImages.map((image, index) => (
                    <div
                      key={index}
                      className="relative outline-none flex justify-center"
                    >
                      <div className="relative w-full h-[65vh] md:h-[70vh]">
                        <Image
                          src={image}
                          alt="Ticket View"
                          fill
                          className="object-contain"
                          priority
                          sizes="100vw"
                        />
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>

              {/* Thumbnails */}
              <div className="w-full max-w-xl px-6 h-20 md:h-24">
                <Slider
                  asNavFor={nav1!}
                  ref={slider2}
                  initialSlide={selectedIndex}
                  slidesToShow={
                    activeImages.length < 5 ? activeImages.length : 5
                  }
                  swipeToSlide={true}
                  focusOnSelect={true}
                  infinite={activeImages.length > 1}
                  centerMode={true}
                  centerPadding="0px"
                  variableWidth={true}
                  arrows={false}
                  className="thumb-nav-slider"
                  responsive={[
                    { breakpoint: 1024, settings: { slidesToShow: 5 } },
                    { breakpoint: 768, settings: { slidesToShow: 4 } },
                    { breakpoint: 480, settings: { slidesToShow: 3 } },
                  ]}
                >
                  {activeImages.map((image, index) => (
                    <div
                      key={index}
                      className="px-2 py-1 focus:outline-none cursor-pointer group !w-auto"
                    >
                      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border-2 border-transparent transition-all opacity-30 [.slick-current_&]:opacity-100 [.slick-current_&]:border-orange-500 [.slick-current_&]:scale-110 group-hover:opacity-70 group-hover:scale-105 shadow-sm">
                        <Image
                          src={image}
                          alt="Thumbnail"
                          fill
                          className="object-cover"
                          sizes="100px"
                        />
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx global>{`
        .gallery-main-slider .slick-slide {
          padding: 0 10px;
        }
        .thumb-nav-slider .slick-track {
          display: flex !important;
          align-items: center !important;
        }
      `}</style>
    </>
  );
}

// Custom Navigation Arrows for Slick Slider
function NextArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute right-[-20px] md:right-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronRight
        size={30}
        className="group-hover:translate-x-1 transition-transform"
      />
    </button>
  );
}

function PrevArrow({ onClick }: any) {
  return (
    <button
      onClick={onClick}
      className="absolute left-[-20px] md:left-[-40px] top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-orange-600 p-3 rounded-full text-white backdrop-blur-xl transition-all border border-white/10 flex items-center justify-center group"
    >
      <ChevronLeft
        size={30}
        className="group-hover:-translate-x-1 transition-transform"
      />
    </button>
  );
}
